/**
 * Zod schemas for every response that crosses the API boundary. Each schema is
 * annotated with the interface it must produce so TypeScript flags drift between
 * the hand-written types and the runtime validation.
 */
import { z } from "zod";
import type {
  AddAssignedRatingsResult,
  BuildLookupRow,
  CatalogSearchResult,
  CensusImportPreview,
  ConditionGuidance,
  ConditionSearchHit,
  CreateGroupResponse,
  CurrentUser,
  ExpectedDebitsBand,
  ExpectedDebitsTable,
  FlagChangeResult,
  GroupDetail,
  GroupHistorySummary,
  GroupMember,
  GroupSummary,
  GroupTotals,
  GroupWithMembers,
  MedicalCondition,
  MemberAssignedRating,
  MemberTotals,
  MemberWithRatings,
  ModificationReportRow,
  MugsManualApplyResult,
  MugsManualChange,
  MugsManualChangeSummary,
  MugsManualRelease,
  OpportunitySummary,
  PagedResult,
  PriorYearRating,
  Rating,
  RatingWithEffectiveDebits,
  ReopenGroupResult,
  RestoreVersionResult,
  RmmPreview,
  SalesCensus,
  SendToSalesResult,
  SmartSearchResult,
  ToggleMemberExcludedResult,
  WorkbenchUser,
} from "../types";
import { CHANGE_REASONS, CREATED_PROCESSES, GENDERS, GROUP_IHQ_TYPES, GROUP_STATUSES, IHQ_TYPES, MATCH_TIERS, MEMBER_TYPES, RATING_SOURCES, RELATIONS, RELATION_CLASSES, USER_ROLES } from "../types";

const nullableString = z.string().nullable();
const nullableNumber = z.number().nullable();
const skynetVersion = z.union([z.literal(0), z.literal(1)]);

export const WorkbenchUserSchema: z.ZodType<WorkbenchUser> = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  role: z.enum(USER_ROLES),
});

export const CurrentUserSchema: z.ZodType<CurrentUser> = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  role: z.enum(USER_ROLES),
  recentGroupIds: z.array(z.string()),
});

export const GroupSummarySchema: z.ZodType<GroupSummary> = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(GROUP_STATUSES),
  version: z.number(),
  supersededByGroupId: nullableString,
  underwriterUserId: nullableString,
  underwriterName: nullableString,
  quoteOwnerName: z.string(),
  quoteOwnerUserId: z.string(),
  effectiveDate: z.string(),
  ihqType: z.enum(GROUP_IHQ_TYPES),
  notes: nullableString,
  skynetVersion,
  createdProcess: z.enum(CREATED_PROCESSES),
  createdUtc: z.string(),
  completedUtc: nullableString,
  memberCount: z.number(),
  totalDebits: z.number(),
  opportunityId: z.string(),
  lastOpenedUtc: nullableString,
});

export function pagedResultSchema<T>(item: z.ZodType<T>): z.ZodType<PagedResult<T>> {
  return z.object({ items: z.array(item), page: z.number(), pageSize: z.number(), totalCount: z.number() });
}

export const MemberAssignedRatingSchema: z.ZodType<MemberAssignedRating> = z.object({
  id: z.string(),
  memberId: z.string(),
  ratingId: nullableNumber,
  conditionId: z.number(),
  conditionName: z.string(),
  ratingText: z.string(),
  time: nullableString,
  defaultDebits: z.number(),
  assignedDebits: z.number(),
  isMedication: z.boolean(),
  uwDollars: nullableNumber,
  isExcluded: z.boolean(),
  irixSource: z.boolean(),
  notes: nullableString,
  source: z.enum(RATING_SOURCES),
  changeReason: z.enum(CHANGE_REASONS).nullable(),
  changeExplanation: nullableString,
  wasOnSkynetOriginal: z.boolean(),
  substitutedFromRatingId: nullableNumber,
  createdUtc: z.string(),
  modifiedUtc: z.string(),
});

const groupMemberShape = {
  id: z.string(),
  groupId: z.string(),
  externalMemberId: nullableString,
  alliedMemberNumber: nullableString,
  firstName: z.string(),
  lastName: z.string(),
  relation: z.enum(RELATIONS),
  subscriberMemberId: nullableString,
  ssnMasked: nullableString,
  notes: nullableString,
  isExcluded: z.boolean(),
  isReviewed: z.boolean(),
  irixChecked: z.boolean(),
  confirmedValidAuth: z.boolean(),
  memberType: z.enum(MEMBER_TYPES),
  cohortYear: nullableNumber,
  cohortGender: z.enum(GENDERS).nullable(),
  wasOnSkynetOriginal: z.boolean(),
  gender: z.enum(GENDERS).nullable(),
  dateOfBirth: nullableString,
  heightFeet: nullableNumber,
  heightInches: nullableNumber,
  weightLbs: nullableNumber,
  postalCode: nullableString,
  createdUtc: z.string(),
  modifiedUtc: z.string(),
};

export const GroupMemberSchema: z.ZodType<GroupMember> = z.object(groupMemberShape);

export const MemberWithRatingsSchema: z.ZodType<MemberWithRatings> = z.object({ ...groupMemberShape, assignedRatings: z.array(MemberAssignedRatingSchema) });

export const SalesCensusSchema: z.ZodType<SalesCensus> = z.object({
  groupId: z.string(),
  version: z.number(),
  opportunityId: z.string(),
  sentUtc: z.string(),
  sentByName: z.string(),
  rmm: z.number(),
  adjustedRmm: z.number(),
  totalDebits: z.number(),
  members: z.array(
    z.object({
      censusMemberId: z.string(),
      sourceMemberIds: z.array(z.string()),
      externalMemberId: nullableString,
      fullName: z.string(),
      relation: z.enum(RELATIONS),
      dateOfBirth: nullableString,
      totalDebits: z.number(),
    }),
  ),
});

export const GroupWithMembersSchema: z.ZodType<GroupWithMembers> = z.object({
  id: z.string(),
  webMugsGroupNumber: nullableString,
  name: z.string(),
  effectiveDate: z.string(),
  ihqType: z.enum(GROUP_IHQ_TYPES),
  notes: nullableString,
  opportunityId: z.string(),
  opportunityName: z.string(),
  accountName: z.string(),
  isRenewal: z.boolean(),
  quoteId: nullableString,
  underwriterUserId: nullableString,
  underwriterName: nullableString,
  quoteOwnerName: z.string(),
  quoteOwnerUserId: z.string(),
  skynetVersion,
  isAiV2Copy: z.boolean(),
  sourceGroupId: nullableString,
  externalKey: nullableString,
  status: z.enum(GROUP_STATUSES),
  version: z.number(),
  supersededByGroupId: nullableString,
  uwOverrideMultiplier: z.number(),
  expectedDebitsTableId: nullableString,
  hasSrxCarveOut: z.boolean(),
  isDeleted: z.boolean(),
  createdUtc: z.string(),
  createdByUserId: z.string(),
  modifiedUtc: z.string(),
  modifiedByUserId: z.string(),
  completedUtc: nullableString,
  sentCensus: SalesCensusSchema.nullable(),
  members: z.array(MemberWithRatingsSchema),
});

export const MemberTotalsSchema: z.ZodType<MemberTotals> = z.object({
  memberId: z.string(),
  age: nullableNumber,
  build: z.number(),
  rxDebits: z.number(),
  conditionDebits: z.number(),
  totalDebits: z.number(),
  uwDollars: z.number(),
  expectedDebits: z.number(),
  baseDebits: z.number(),
});

export const GroupTotalsSchema: z.ZodType<GroupTotals> = z.object({
  employees: z.number(),
  spouses: z.number(),
  children: z.number(),
  totalMembers: z.number(),
  membersExcluded: z.number(),
  totalDebits: z.number(),
  rmm: z.number(),
  adjustedRmm: z.number(),
  uwDollars: z.number(),
  perMember: z.record(z.string(), MemberTotalsSchema),
});

export const GroupDetailSchema: z.ZodType<GroupDetail> = z.object({ group: GroupWithMembersSchema, totals: GroupTotalsSchema });

export const ToggleMemberExcludedResultSchema: z.ZodType<ToggleMemberExcludedResult> = z.object({ detail: GroupDetailSchema, autoExcludedSpockMemberIds: z.array(z.string()) });

export const FlagChangeResultSchema: z.ZodType<FlagChangeResult> = z.object({ groupStatus: z.enum(GROUP_STATUSES) });

export const AddAssignedRatingsResultSchema: z.ZodType<AddAssignedRatingsResult> = z.object({
  detail: GroupDetailSchema,
  created: z.array(MemberAssignedRatingSchema),
  updated: z.array(MemberAssignedRatingSchema),
  autoAdded: z.array(MemberAssignedRatingSchema),
  substitutions: z.array(z.object({ brandRatingId: z.number(), brandName: z.string(), genericRatingId: z.number(), genericName: z.string(), debits: z.number() })),
});

export const SendToSalesResultSchema: z.ZodType<SendToSalesResult> = z.object({
  detail: GroupDetailSchema,
  census: SalesCensusSchema,
  supersededGroupIds: z.array(z.string()),
});

export const RmmPreviewSchema: z.ZodType<RmmPreview> = z.object({ rmm: z.number(), adjustedRmm: z.number(), totalDebits: z.number(), includedMembers: z.number() });

export const ReopenGroupResultSchema: z.ZodType<ReopenGroupResult> = z.object({ detail: GroupDetailSchema, previousVersionGroupId: z.string() });

export const RestoreVersionResultSchema: z.ZodType<RestoreVersionResult> = z.object({ detail: GroupDetailSchema, sourceGroupId: z.string(), replacedGroupId: nullableString });

export const OpportunitySummarySchema: z.ZodType<OpportunitySummary> = z.object({
  id: z.string(),
  name: z.string(),
  accountName: z.string(),
  isRenewal: z.boolean(),
  quoteOwnerName: z.string(),
  quoteOwnerUserId: z.string(),
  effectiveDate: z.string(),
});

export const GroupHistorySummarySchema: z.ZodType<GroupHistorySummary> = z.object({
  previouslyUnderwrittenCount: z.number(),
  previouslySold: z.boolean(),
  mostRecentRmm: nullableNumber,
  mostRecentGroupId: nullableString,
  mostRecentGroupName: nullableString,
});

export const PriorYearRatingSchema: z.ZodType<PriorYearRating> = z.object({ conditionName: z.string(), ratingText: z.string(), debits: z.number(), planYear: z.string() });

export const CensusImportPreviewSchema: z.ZodType<CensusImportPreview> = z.object({
  fileName: z.string(),
  rows: z.array(
    z.object({
      rowNumber: z.number(),
      name: z.string(),
      externalId: nullableString,
      relation: z.string(),
      gender: nullableString,
      dateOfBirth: nullableString,
      heightFeet: nullableNumber,
      heightInches: nullableNumber,
      weightLbs: nullableNumber,
      ssn: nullableString,
      notes: nullableString,
      errors: z.array(z.string()),
    }),
  ),
});

export const CreateGroupResponseSchema: z.ZodType<CreateGroupResponse> = z.object({
  groupId: z.string(),
  skynetVersionGroupId: nullableString,
  groupUrl: z.string(),
  status: z.string(),
  created: z.boolean(),
  totals: z.object({
    employees: z.number(),
    spouses: z.number(),
    children: z.number(),
    totalMembers: z.number(),
    membersExcluded: z.number(),
    totalDebits: z.number(),
    rmm: z.number(),
    adjustedRmm: z.number(),
  }),
  members: z.array(z.object({ externalMemberId: nullableString, memberId: z.string(), totalDebits: z.number(), buildPoints: z.number(), rxDebits: z.number(), conditionDebits: z.number() })),
  warnings: z.array(z.string()),
});

export const MedicalConditionSchema: z.ZodType<MedicalCondition> = z.object({
  id: z.number(),
  guidelineConditionNumber: nullableString,
  name: z.string(),
  category: z.string(),
  description: nullableString,
  developmentQuestions: nullableString,
  notes: nullableString,
  seeConditionId: nullableNumber,
  isCustom: z.boolean(),
  isActive: z.boolean(),
  requiresDoubleBuild: z.boolean(),
});

export const RatingSchema: z.ZodType<Rating> = z.object({
  id: z.number(),
  guidelineRatingNumber: nullableString,
  conditionId: z.number(),
  ratingText: z.string(),
  time: nullableString,
  code: nullableString,
  baseDebits: z.number(),
  notes: nullableString,
  isMedication: z.boolean(),
  isCustom: z.boolean(),
  introducedReleaseName: nullableString,
  isActive: z.boolean(),
});

export const ConditionSearchHitSchema: z.ZodType<ConditionSearchHit> = z.object({
  conditionId: z.number(),
  name: z.string(),
  category: z.string(),
  isCustom: z.boolean(),
  isActive: z.boolean(),
  selectionCount: z.number(),
  matchTier: z.enum(MATCH_TIERS),
  matchedText: nullableString,
});

export const SmartSearchResultSchema: z.ZodType<SmartSearchResult> = z.object({
  query: z.string(),
  hits: z.array(z.object({ conditionId: z.number(), name: z.string(), isCustom: z.boolean(), matchedRatingText: z.string(), indexHits: z.number() })),
  unmappedIndexConditions: z.number(),
  timings: z.object({ embeddingMs: z.number(), searchMs: z.number(), mappingMs: z.number(), totalMs: z.number() }),
});

export const CatalogSearchResultSchema: z.ZodType<CatalogSearchResult> = z.object({
  query: z.string(),
  tiers: z.array(z.object({ tier: z.enum(MATCH_TIERS), hits: z.array(ConditionSearchHitSchema) })),
});

export const RatingWithEffectiveDebitsSchema: z.ZodType<RatingWithEffectiveDebits> = z.object({
  ratingId: z.number(),
  conditionId: z.number(),
  ratingText: z.string(),
  time: nullableString,
  baseDebits: z.number(),
  effectiveDebits: z.number(),
  modification: z.object({ multiplier: nullableNumber, overrideDebits: nullableNumber, notes: z.string() }).nullable(),
  isMedication: z.boolean(),
  isCustom: z.boolean(),
  isActive: z.boolean(),
  notes: nullableString,
  selectionCount: z.number(),
  indications: z.array(z.string()),
  genericSubstitute: z.object({ ratingId: z.number(), ratingText: z.string(), effectiveDebits: z.number() }).nullable(),
  specialtyDrug: z.object({ drugName: z.string(), isPapEligible: z.boolean() }).nullable(),
});

export const ConditionGuidanceSchema: z.ZodType<ConditionGuidance> = z.object({
  conditionId: z.number(),
  skynetRule: nullableString,
  underwritingRule: nullableString,
  guideline: z.object({ description: nullableString, developmentQuestions: nullableString, notes: nullableString }).nullable(),
});

const ExpectedDebitsBandSchema: z.ZodType<ExpectedDebitsBand> = z.object({
  id: z.string(),
  relationClass: z.enum(RELATION_CLASSES),
  ageStart: z.number(),
  ageEnd: z.number(),
  maleExpected: z.number(),
  maleBase: z.number(),
  femaleExpected: z.number(),
  femaleBase: z.number(),
  compositeExpected: z.number(),
  compositeBase: z.number(),
});

export const ExpectedDebitsTableSchema: z.ZodType<ExpectedDebitsTable> = z.object({
  id: z.string(),
  name: z.string(),
  notes: nullableString,
  ihqType: z.enum(IHQ_TYPES),
  isActive: z.boolean(),
  sourceTableId: nullableString,
  createdUtc: z.string(),
  createdByUserId: z.string(),
  createdByName: z.string(),
  bands: z.array(ExpectedDebitsBandSchema),
});

export const BuildLookupRowSchema: z.ZodType<BuildLookupRow> = z.object({
  isJuvenile: z.boolean(),
  gender: z.enum(GENDERS).nullable(),
  heightInchesTotal: z.number(),
  weightMin: z.number(),
  weightMax: z.number(),
  points: z.number(),
});

export const ModificationReportRowSchema: z.ZodType<ModificationReportRow> = z.object({
  id: z.string(),
  kind: z.enum(["Custom Condition", "Custom Rating", "Debit Modification"]),
  conditionId: z.number(),
  conditionName: z.string(),
  ratingId: nullableNumber,
  ratingText: nullableString,
  detail: z.string(),
  authorName: z.string(),
  createdUtc: z.string(),
});

const MugsManualSourceSchema = z.enum(["Milliman", "WebMugsArchive", "CurrentCatalogSeed", "CustomSeed"]);
const MugsManualStatusSchema = z.enum(["Staged", "Applied", "Discarded"]);
const MugsManualChangeKindSchema = z.enum(["NewCondition", "NewRating", "RemovedCondition", "RemovedRating", "DebitsChanged", "NotesChanged", "CodeChanged", "Renamed", "TimeChanged", "TimeBandsSplit", "DuplicateSourceRows"]);
const MugsManualChangeDecisionSchema = z.enum(["Approve", "Ignore"]);

export const MugsManualReleaseSchema: z.ZodType<MugsManualRelease> = z.object({
  id: z.string(),
  name: z.string(),
  effectiveFrom: nullableString,
  source: MugsManualSourceSchema,
  status: MugsManualStatusSchema,
  isCurrent: z.boolean(),
  loadedUtc: z.string(),
  loadedByUserId: z.string(),
  appliedUtc: nullableString,
  appliedByUserId: nullableString,
  conditions: z.number(),
  ratings: z.number(),
  changes: z.number(),
  ignored: z.number(),
});

export const MugsManualChangeSummarySchema: z.ZodType<MugsManualChangeSummary> = z.object({
  mugsManualId: z.string(),
  name: z.string(),
  status: MugsManualStatusSchema,
  counts: z.array(z.object({ kind: MugsManualChangeKindSchema, total: z.number(), ignored: z.number() })),
  ignoredCount: z.number(),
  warnings: z.array(z.string()),
});

export const MugsManualChangeSchema: z.ZodType<MugsManualChange> = z.object({
  id: z.string(),
  mugsManualId: z.string(),
  changeKind: MugsManualChangeKindSchema,
  conditionId: z.number().nullable(),
  ratingId: z.number().nullable(),
  sourceCdesNo: z.number().nullable(),
  sourceConNbr: z.number().nullable(),
  conditionName: z.string(),
  ratingText: nullableString,
  time: nullableString,
  oldValue: nullableString,
  newValue: nullableString,
  hasActiveModification: z.boolean(),
  decision: MugsManualChangeDecisionSchema,
  decidedByUserId: nullableString,
  decidedUtc: nullableString,
});

export const MugsManualApplyResultSchema: z.ZodType<MugsManualApplyResult> = z.object({
  mugsManualId: z.string(),
  conditionsCreated: z.number(),
  conditionsUpdated: z.number(),
  conditionsRetired: z.number(),
  ratingsCreated: z.number(),
  ratingsUpdated: z.number(),
  ratingsRetired: z.number(),
  overridesCreated: z.number(),
  changesApplied: z.number(),
  changesIgnored: z.number(),
  warnings: z.array(z.string()),
});
